import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, MapPin, Mail, ClipboardList } from 'lucide-react'
import { Button, Input, Card, CardHeader, CardTitle, CardDescription } from '../common'
import useSurveyStore from '../../stores/surveyStore'
import useSettingsStore from '../../stores/settingsStore'

// Survey level descriptions
const SURVEY_LEVELS = [
  { value: 1, label: 'Level 1', description: 'Basic visual inspection' },
  { value: 2, label: 'Level 2', description: 'Standard home survey' },
  { value: 3, label: 'Level 3', description: 'Full building survey' }
]

export default function PropertyForm({ initialData = null, onSubmit }) {
  const navigate = useNavigate()
  const createSurvey = useSurveyStore(state => state.createSurvey)
  const updateSurvey = useSurveyStore(state => state.updateSurvey)
  const defaultSurveyor = useSettingsStore(state => state.defaultSurveyor)

  const [formData, setFormData] = useState({
    propertyName: initialData?.propertyName || '',
    address: initialData?.address || '',
    postcode: initialData?.postcode || '',
    surveyorName: initialData?.surveyorName || defaultSurveyor,
    surveyLevel: initialData?.surveyLevel || 1
  })

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validate = () => {
    const newErrors = {}

    if (!formData.propertyName.trim()) {
      newErrors.propertyName = 'Property name is required'
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required'
    }

    if (!formData.postcode.trim()) {
      newErrors.postcode = 'Postcode is required'
    }

    if (!formData.surveyorName.trim()) {
      newErrors.surveyorName = 'Surveyor name is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) return

    setLoading(true)
    try {
      let survey
      if (initialData?.id) {
        // Update existing survey
        survey = await updateSurvey(initialData.id, formData)
      } else {
        // Create new survey
        survey = await createSurvey(formData)
      }

      if (onSubmit) {
        onSubmit(survey)
      } else {
        navigate(`/survey/${survey.id}`)
      }
    } catch (error) {
      console.error('Failed to save survey:', error)
      setErrors({ submit: 'Failed to save survey. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {initialData ? 'Edit Property Details' : 'Property Details'}
        </CardTitle>
        <CardDescription>
          Enter the property information for this survey
        </CardDescription>
      </CardHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Property Name"
          name="propertyName"
          placeholder="e.g., Smith Residence, 123 High Street"
          value={formData.propertyName}
          onChange={handleChange}
          error={errors.propertyName}
          icon="Building2"
        />

        <Input
          label="Full Address"
          name="address"
          placeholder="e.g., 123 High Street, London"
          value={formData.address}
          onChange={handleChange}
          error={errors.address}
          icon="MapPin"
        />

        <Input
          label="Postcode"
          name="postcode"
          placeholder="e.g., SW1A 1AA"
          value={formData.postcode}
          onChange={handleChange}
          error={errors.postcode}
          className="uppercase"
        />

        <Input
          label="Surveyor Name"
          name="surveyorName"
          placeholder="Enter surveyor name"
          value={formData.surveyorName}
          onChange={handleChange}
          error={errors.surveyorName}
          icon="User"
        />

        {/* Survey Level Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">
            Survey Level <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-3">
            {SURVEY_LEVELS.map((level) => (
              <button
                key={level.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, surveyLevel: level.value }))}
                className={`
                  p-3 rounded-lg border-2 text-center transition-all
                  ${formData.surveyLevel === level.value
                    ? 'border-primary-700 bg-primary-50 text-primary-700'
                    : 'border-slate-200 hover:border-slate-300 text-slate-600'
                  }
                `}
              >
                <div className="font-semibold">{level.label}</div>
                <div className="text-xs mt-1 opacity-75">{level.description}</div>
              </button>
            ))}
          </div>
        </div>

        {errors.submit && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{errors.submit}</p>
          </div>
        )}

        <div className="flex gap-4 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="flex-1"
          >
            {initialData ? 'Save Changes' : 'Start Survey'}
          </Button>
        </div>
      </form>
    </Card>
  )
}
